import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuthContext } from './AuthContext';
import { supabase } from '../lib/supabase';
import { retry } from '../utils/async';
import type { Task, TaskStatus, Role, CustomRole, TaskLabel } from '../types';

interface TaskContextType {
  tasks: Task[];
  roles: CustomRole[];
  labels: TaskLabel[];
  loading: boolean;
  error: Error | null;
  addTask: (task: Omit<Task, 'id' | 'status'>) => Promise<void>;
  updateTask: (taskId: string, updates: Partial<Task>) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  changeTaskStatus: (taskId: string, status: TaskStatus) => Promise<void>;
  updateRole: (roleId: Role, updates: { name: string; color: string }) => Promise<void>;
  addLabel: (label: Omit<TaskLabel, 'id'>) => Promise<void>;
  deleteLabel: (labelId: string) => Promise<void>;
}

const TaskContext = createContext<TaskContextType | undefined>(undefined);

const DEFAULT_ROLES: CustomRole[] = [
  { id: 'work', name: 'Work', color: '#4b65de' },
  { id: 'family', name: 'Family', color: '#ef4444' },
  { id: 'personal', name: 'Personal', color: '#9333ea' }
];

export function TaskProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [labels, setLabels] = useState<TaskLabel[]>([]);
  const [roles, setRoles] = useState<CustomRole[]>([
    { id: 'work', name: 'Work', color: '#4b65de' },
    { id: 'family', name: 'Family', color: '#ef4444' },
    { id: 'personal', name: 'Personal', color: '#9333ea' }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch custom roles when user is authenticated
  useEffect(() => {
    if (user) {
      fetchTasks();
      fetchRoles();
      
      const subscription = supabase
        .channel('tasks_changes')
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'tasks' },
          fetchTasks
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user]);

  const fetchRoles = async () => {
    try {
      const { data: customRoles, error: rolesError } = await retry(
        () => supabase
          .from('custom_roles')
          .select('*')
          .eq('user_id', user?.id),
        3,
        1000
      );

      if (rolesError) throw rolesError;

      // Update roles with custom names/colors if they exist
      if (customRoles?.length) {
        setRoles(prev => prev.map(role => {
          const customRole = customRoles.find(cr => cr.role === role.id);
          return customRole ? {
            ...role,
            name: customRole.name,
            color: customRole.color
          } : role;
        }));
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Keep default roles on error
      if (roles.length === 0) {
        setRoles(DEFAULT_ROLES);
      }
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const { data, error } = await retry(
        () => supabase
          .from('tasks')
          .select('*')
          .eq('user_id', user?.id)
          .order('created_at', { ascending: false }),
        3,
        1000
      );

      if (error) throw error;
      setTasks(data || []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err as Error);
      // Keep existing tasks on error
      if (tasks.length === 0) {
        setTasks([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const addTask = async (task: Omit<Task, 'id' | 'status'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Insert task
      const { data, error } = await supabase
        .from('tasks')
        .insert([{
          ...task,
          user_id: user.id,
          status: 'todo'
        }])
        .select()
        .single();

      if (error) throw error;


      setTasks(prev => [data, ...prev]);
    } catch (err) {
      throw err;
    }
  };

  const updateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', taskId)
        .eq('user_id', user?.id);

      if (error) throw error;
      setTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, ...updates } : task
      ));
    } catch (err) {
      throw err;
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId)
        .eq('user_id', user?.id);

      if (error) throw error;
      setTasks(prev => prev.filter(task => task.id !== taskId));
    } catch (err) {
      throw err;
    }
  };

  const changeTaskStatus = async (taskId: string, status: TaskStatus) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ status })
        .eq('id', taskId)
        .eq('user_id', user?.id);

      if (error) throw error;
      setTasks(prev => prev.map(task =>
        task.id === taskId ? { ...task, status } : task
      ));
    } catch (err) {
      throw err;
    }
  };

  const fetchCustomRoles = async () => {
    try {
      const { data, error } = await supabase
        .from('custom_roles')
        .select('*')
        .eq('user_id', user?.id);

      if (error) throw error;

      if (data && data.length > 0) {
        // Update roles with custom names/colors while preserving IDs
        setRoles(prev => prev.map(role => {
          const customRole = data.find(cr => cr.role === role.id);
          return customRole ? {
            ...role,
            name: customRole.name,
            color: customRole.color
          } : role;
        }));
      }
    } catch (err) {
      console.error('Error fetching custom roles:', err);
    }
  };

  const updateRole = async (roleId: Role, updates: { name: string; color: string }) => {
    try {
      // Update or insert custom role in database
      const { error } = await supabase
        .from('custom_roles')
        .upsert({
          user_id: user?.id,
          role: roleId,
          name: updates.name,
          color: updates.color
        }, {
          onConflict: 'user_id,role'
        });

      if (error) throw error;

      // Update local state
      setRoles(prev => prev.map(role =>
        role.id === roleId ? { ...role, ...updates } : role
      ));

    } catch (err) {
      console.error('Error updating role:', err);
      throw err;
    }
  };

  const addLabel = async (label: Omit<TaskLabel, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('task_labels')
        .insert([{
          user_id: user?.id,
          name: label.name,
          color: label.color
        }])
        .select()
        .single();

      if (error) throw error;
      setLabels(prev => [...prev, data]);
    } catch (err) {
      console.error('Error adding label:', err);
      throw err;
    }
  };

  const deleteLabel = async (labelId: string) => {
    try {
      const { error } = await supabase
        .from('task_labels')
        .delete()
        .eq('id', labelId)
        .eq('user_id', user?.id);

      if (error) throw error;
      setLabels(prev => prev.filter(label => label.id !== labelId));
    } catch (err) {
      console.error('Error deleting label:', err);
      throw err;
    }
  };

  return (
    <TaskContext.Provider value={{
      tasks,
      roles,
      labels,
      loading,
      error,
      addTask,
      updateTask,
      deleteTask,
      changeTaskStatus,
      updateRole,
      addLabel,
      deleteLabel,
    }}>
      {children}
    </TaskContext.Provider>
  );
}

export function useTaskContext() {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTaskContext must be used within a TaskProvider');
  }
  return context;
}